import { EmptyState } from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { api } from '../api/client.js';

interface NotificationDto {
  id: string;
  type: string;
  title: string;
  url: string | null;
  readAt: string | null;
  createdAt: string;
}

function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}

/** Glocke in der Topbar: ungelesene Anzahl, Liste, klick → öffnen + gelesen. */
export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const feed = useQuery({
    queryKey: ['notifications'],
    refetchInterval: 60_000,
    queryFn: () => api<{ items: NotificationDto[]; unread: number }>('/api/v1/notifications'),
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) =>
      api('/api/v1/notifications/read', { method: 'POST', body: ids ? { ids } : {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent): void => {
      if (popRef.current && !popRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const unread = feed.data?.unread ?? 0;
  const items = feed.data?.items ?? [];

  return (
    <div className="topmenu" ref={popRef}>
      <button
        type="button"
        className="topbar-btn topbar-btn--bell"
        aria-label={unread > 0 ? `Benachrichtigungen (${unread} ungelesen)` : 'Benachrichtigungen'}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell size={16} aria-hidden />
        {unread > 0 ? <span className="bell__badge">{unread > 9 ? '9+' : unread}</span> : null}
      </button>
      {open ? (
        <div className="topmenu__pop topmenu__pop--wide" role="menu">
          <div className="bell__head">
            <span className="topmenu__section">Benachrichtigungen</span>
            {unread > 0 ? (
              <button
                type="button"
                className="bell__readall"
                onClick={() => markRead.mutate(undefined)}
              >
                Alle gelesen
              </button>
            ) : null}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: 'var(--au-space-3)' }}>
              <EmptyState
                title="Keine Benachrichtigungen"
                description="Hier erscheinen Zuweisungen, gewonnene Deals & Co."
              />
            </div>
          ) : (
            <ul className="bell__list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`bell__item${item.readAt === null ? ' bell__item--unread' : ''}`}
                    onClick={() => {
                      markRead.mutate([item.id]);
                      setOpen(false);
                      if (item.url !== null) void navigate(item.url);
                    }}
                  >
                    <span className="bell__dot" aria-hidden />
                    <span className="bell__title">{item.title}</span>
                    <span className="bell__time">{relativeTime(item.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
